/**
 * Felszereltség összevonás import / űrlap merge-nél.
 * Szabályok: backend/listing_features.py (szinonima lista szinkronban tartandó).
 */
import { ALL_FEATURE_KEYS, CAR_FEATURE_GROUPS } from "@/lib/carFeatures";

const KEY_TO_LABEL = Object.fromEntries(
  CAR_FEATURE_GROUPS.flatMap((g) => g.items.map((i) => [i.key, i.label])),
);
const LABEL_TO_KEY = Object.fromEntries(
  CAR_FEATURE_GROUPS.flatMap((g) => g.items.map((i) => [i.label.toLowerCase(), i.key])),
);

/** @type {[RegExp, string][]} */
const ALIAS_TO_KEY = [
  [/távolságtartó|adaptív\s*tempomat|acc\b|adaptive cruise/i, "equip_adaptive_cruise"],
  [/automata\s*klíma|auto\s*klíma|klímabőr/i, "equip_ac_auto"],
  [/manuális\s*klíma/i, "equip_ac_manual"],
  [/kétzónás|2\s*zónás|dual zone|többzónás/i, "equip_dual_climate"],
  [/apple\s*carplay|\bcarplay\b/i, "equip_apple_carplay"],
  [/android\s*auto/i, "equip_android_auto"],
  [/gyári\s*navig|beépített\s*navig|\bnavigáció\b|\bnavigation\b/i, "equip_navigation"],
  [/360|körkamera|surround\s*view/i, "equip_360_camera"],
  [/tolató\s*kamera|tolatokamera|hátsó\s*kamera|rear\s*camera/i, "equip_rear_camera"],
  [/parkoló\s*radar|parkolóradar|\bpdc\b/i, "equip_parking_sensors"],
  [/\btempomat\b|cruise\s*control/i, "equip_cruise_control"],
  [/panoráma|panorama\s*tető|üvegtető|nappali\s*tető/i, "equip_panoramic_roof"],
  [/bőr\s*ülés|bőrkárpit|valódi\s*bőr|bőr belső/i, "equip_leather"],
  [/bluetooth/i, "equip_bluetooth"],
  [/vonóhorog|vonó\s*horog|\bahk\b/i, "equip_tow_hitch"],
  [/4x4|xdrive|quattro|4matic|összkerék|\bawd\b/i, "equip_awd"],
  [/xenon|led\s*fén|matrix\s*led/i, "equip_led_lights"],
  [/könnyűfém|alufelni|\bfelnik\b|alloy/i, "equip_alloy_wheels"],
  [/elektromos\s*ablakok?\s*és\s*tükrök|elektromos\s*tükör|\btükrök\b/i, "equip_electric_mirrors"],
  [/sport\s*csomag/i, "equip_sport_package"],
  [/első\s*tulaj/i, "equip_one_owner"],
  [/szerviz\s*könyv|szervizkönyv/i, "paper_service_book"],
  [/\bisofix\b/i, "__extra__ISOFIX"],
  [/multifunkciós\s*kormány/i, "__extra__Multifunkciós kormánykerék"],
  [/esőszenzor/i, "__extra__Esőszenzor"],
  [/menetfény/i, "__extra__Menetfény"],
  [/automata\s*fényszóró/i, "__extra__Automata fényszórókapcsolás"],
];

const JUNK_FEATURE_RE =
  /hirdetés\s*azonosít|hasznaltauto|használtautó|logo|regisztráció|belépés|főoldal|találati|szolgáltatásaink|cookie|adatvédelem|^hirdetés$|^hasznos$|^kevesebb$|^beltér$|^kültér$|hasonló\s*hirdetés/i;

const EQUIPMENT_HINT_RE =
  /isofix|radar|kamera|klíma|szenzor|automata|elektrom|bluetooth|navig|ülés|kormány|fén|lámpa|tükör|ablak|parkol|tempomat|airbag|mp3|hifi|hangszór|felni|esp|abs|blokkolás|menetstabil/i;

function isJunkFeatureLabel(text) {
  const t = (text || "").trim();
  if (!t || t.length > 120) return true;
  if (JUNK_FEATURE_RE.test(t)) return true;
  if (/^hirdetésazonosító\s*:\s*\d+/i.test(t)) return true;
  return false;
}

function isValidEquipmentExtra(text) {
  const t = (text || "").trim();
  if (!t || isJunkFeatureLabel(t)) return false;
  if (resolveFeatureKey(t)) return true;
  if (t.length < 3 || t.length > 90) return false;
  if (t.includes("(") && t.includes(")")) return true;
  return EQUIPMENT_HINT_RE.test(t);
}

export function normalizeFeatureText(s) {
  return (s || "")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s+°]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokens(s) {
  return new Set(
    normalizeFeatureText(s)
      .split(" ")
      .filter((w) => w.length >= 2),
  );
}

export function resolveFeatureKey(text) {
  const raw = (text || "").trim();
  if (!raw) return null;
  const low = raw.toLowerCase();
  if (ALL_FEATURE_KEYS.has(low)) return low;
  if (LABEL_TO_KEY[low]) return LABEL_TO_KEY[low];
  for (const [re, key] of ALIAS_TO_KEY) {
    if (re.test(raw)) return key;
  }
  const norm = normalizeFeatureText(raw);
  if (norm.length < 4) return null;
  for (const [label, key] of Object.entries(LABEL_TO_KEY)) {
    const ln = normalizeFeatureText(label);
    if (norm === ln || (norm.length >= 6 && (norm.includes(ln) || ln.includes(norm)))) {
      return key;
    }
  }
  const textTok = tokens(raw);
  let best = null;
  let bestScore = 0;
  for (const [key, label] of Object.entries(KEY_TO_LABEL)) {
    const labelTok = tokens(label);
    const overlap = [...textTok].filter((t) => labelTok.has(t)).length;
    if (overlap >= 2 && overlap / Math.max(labelTok.size, 1) >= 0.65) {
      const score = overlap / labelTok.size;
      if (score > bestScore) {
        bestScore = score;
        best = key;
      }
    }
  }
  return best;
}

function resolveConflictingKeys(keySet) {
  const k = new Set(keySet);
  if (k.has("equip_adaptive_cruise")) k.delete("equip_cruise_control");
  if (k.has("equip_ac_auto")) k.delete("equip_ac_manual");
  if (k.has("equip_360_camera")) k.delete("equip_rear_camera");
  if (k.has("equip_dual_climate")) {
    k.delete("equip_ac_manual");
    k.delete("equip_ac_auto");
  }
  return k;
}

function customNearCatalog(label, keySeen) {
  if (resolveFeatureKey(label)) return true;
  const cn = normalizeFeatureText(label);
  if (cn.length < 4) return false;
  for (const key of keySeen) {
    const ln = normalizeFeatureText(KEY_TO_LABEL[key] || "");
    if (cn === ln || (cn.length >= 6 && (cn.includes(ln) || ln.includes(cn)))) return true;
  }
  return false;
}

/** @param {string[]} rawItems @param {{ importMode?: boolean }} opts */
export function coalesceFeatureList(rawItems, { importMode = false } = {}) {
  if (!Array.isArray(rawItems)) return [];
  const pending = rawItems.map((x) => (typeof x === "string" ? x.trim() : "")).filter(Boolean);
  const out = [];
  const keySeen = new Set();
  const customNormSeen = new Set();

  const emitKey = (key) => {
    if (!ALL_FEATURE_KEYS.has(key) || keySeen.has(key)) return;
    const merged = resolveConflictingKeys(new Set([...keySeen, key]));
    for (const drop of [...keySeen]) {
      if (!merged.has(drop)) {
        const idx = out.indexOf(drop);
        if (idx >= 0) out.splice(idx, 1);
        keySeen.delete(drop);
      }
    }
    if (!merged.has(key)) return;
    keySeen.add(key);
    out.push(key);
  };

  for (const item of pending) {
    if (importMode && isJunkFeatureLabel(item)) continue;
    if (item.startsWith("__extra__")) {
      const label = item.slice(9);
      const cn = normalizeFeatureText(label);
      if (cn && !customNormSeen.has(cn)) {
        customNormSeen.add(cn);
        out.push(label);
      }
      continue;
    }
    const mapped = resolveFeatureKey(item);
    if (mapped) {
      emitKey(mapped);
      continue;
    }
    if (item.startsWith("equip_") || item.startsWith("paper_")) {
      if (ALL_FEATURE_KEYS.has(item)) emitKey(item);
      continue;
    }
    const remapped = resolveFeatureKey(item);
    if (remapped) {
      emitKey(remapped);
      continue;
    }
    if (customNearCatalog(item, keySeen)) continue;
    if (importMode && !isValidEquipmentExtra(item)) continue;
    const cn = normalizeFeatureText(item);
    if (!cn || customNormSeen.has(cn) || out.includes(item)) continue;
    customNormSeen.add(cn);
    out.push(item.slice(0, 80));
    if (out.length >= 60) break;
  }
  return out;
}

/** Leírás szöveg → pipák (backend extract_features_from_prose tükör). */
export function extractFeaturesFromProse(text, { importMode = true } = {}) {
  if (!text || !String(text).trim()) return [];
  const items = [];
  for (const [re, key] of ALIAS_TO_KEY) {
    if (re.test(text)) items.push(key);
  }
  if (/elektromos\s*ablak/i.test(text)) items.push("equip_electric_windows");
  for (const chunk of String(text).split(/[·•\n]|;\s*|\|\s*/)) {
    const piece = chunk.trim().replace(/\.$/, "");
    if (piece.length < 3) continue;
    for (const sub of piece.split(/\s+és\s+/)) {
      const s = sub.trim().replace(/\.$/, "");
      if (s.length < 3) continue;
      if (importMode) {
        if (isJunkFeatureLabel(s)) continue;
        const key = resolveFeatureKey(s);
        if (key) items.push(key);
        else if (isValidEquipmentExtra(s)) items.push(s);
      } else {
        items.push(s);
      }
    }
  }
  return coalesceFeatureList(items, { importMode });
}

export function enrichFilledFeatures(filled, extraText = "", { importMode = true } = {}) {
  const desc = filled?.description || "";
  const blob = (extraText || "").trim();
  const proseParts = [desc];
  if (blob && blob !== desc) proseParts.push(blob);
  const merged = coalesceFeatureList(
    [...(filled?.features || []), ...extractFeaturesFromProse(proseParts.join("\n"), { importMode })],
    { importMode },
  );
  return merged.length ? { ...filled, features: merged } : filled;
}
